import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Progress, Title } from "@components";
import { Notification } from "@rio-cloud/rio-uikit";
import { setScheduleDateSelected, setScheduleHoursSelected, useAppSelector, defineRouteId, clearTrip, setIsUptimeEmergency } from "@store";
import { NavigateState } from "@types";

import { ProgressOptions } from "src/components/Progress/Trip";

import { fetchHasMechanicalEmergency } from "src/services/routes";
import { FooterActions, FormAgendamento } from "./components";
import { FormData, FormErrors } from "./types";
import { formatHourFromInput, validateDate, validateDateTime } from "./utils/formUtils";

const TripsSchedule = () => {
  const { scheduleDateSelected, scheduleHoursSelected } = useAppSelector((state) => state.trips);
  const [formData, setFormData] = useState<FormData>({ date: "", hour: "" });
  const [errors, setErrors] = useState<FormErrors>({ date: "", hour: "" });
  const [content] = useState(5);

  const [search] = useSearchParams();
  const currentParams = new URLSearchParams(search);
  const getHashParam = (key: string) => {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx === -1) return null;
    return new URLSearchParams(hash.slice(qIdx + 1)).get(key);
  };
  
  const isUptimeEmergencyStore = useAppSelector((state) => state.trips.isUptimeEmergency);
 
  const isUptimeEmergency = !!search?.get('dn-group-id');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateState: any = location.state as NavigateState | null;

  const setError = (field: keyof FormErrors, message: string) => {
    setErrors(prev => ({ ...prev, [field]: message }));
  };

  const clearError = (field: keyof FormErrors) => {
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleDateChange = (selectedDate: string) => {
    setFormData({
      ...formData,
      date: selectedDate,
    })

    if (formData?.hour) {
      
        setTimeout(() => {
          validateHourField(formData?.hour, selectedDate);
        }, 300)
      }
  };

  const handleHourChange = (input: any) => {
    const formattedTime = formatHourFromInput(input);
    setFormData(prev => ({ ...prev, hour: formattedTime }));
    validateHourField(formattedTime);
  };

  const validateHourField = (time: string, date?: string) => {
    const newDate = date || formData.date;
    const validation = validateDateTime(time, newDate);

    if (validation.isValid) {
      clearError("hour");
    } else if (validation.error) {
      setError("hour", validation.error);
    }
  };

  const isFormValid = () => {
    return formData.date && formData.hour && !errors.date && !errors.hour;
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.hour) {
      setError("date", "Preencha corretamente a data e a hora da viagem.");
      return;
    }

    const hourValidation = validateDateTime(formData.hour, formData.date);
    if (!hourValidation.isValid) {
      setError("hour", hourValidation.error);
      return;
    }

    try {
      dispatch(setScheduleHoursSelected(formData.hour));
      dispatch(setScheduleDateSelected(formData.date));

      navigate(`/routing/trips/driver?${currentParams}`, {
        state: { edit: navigateState?.edit, csv: navigateState?.csv, csvData: navigateState?.csvData },
      });
    } catch (error) {
      console.error("Erro ao processar agendamento:", error);
      return;
    }
  };

  const handleCancel = () => {
    dispatch(defineRouteId(null));
    if (currentParams.get("editTrip")) {
      navigate("/routing/trips/custom?" + currentParams.toString(), {
        state: { edit: navigateState?.edit, csv: navigateState?.csv, csvData: navigateState?.csvData },
      });
      return;
    }
    navigate(-1);
  };

  const pageIndex = 0;
  const handleContentChange = (idx: number) => {
    if (idx > pageIndex + 1) {
      return;
    }

    if (!formData.date || !formData.hour) {
      setError("date", "Preencha corretamente a data e a hora da viagem.");
      return;
    }

    const hourValidation = validateDateTime(formData.hour, formData.date);
    if (!hourValidation.isValid) {
      setError("hour", hourValidation.error);
      return;
    }

    try {
      dispatch(setScheduleHoursSelected(formData.hour));
      dispatch(setScheduleDateSelected(formData.date));

      navigate(ProgressOptions[idx || 0]?.route + "?" + currentParams.toString(), {
        state: { edit: navigateState?.edit, csv: navigateState?.csv },
      });
    } catch (error) {
      console.error("Erro ao processar agendamento:", error);
      return;
    }


  };

  useEffect(() => {
    if (scheduleDateSelected || scheduleHoursSelected) {
      console.log('useEffect',{scheduleDateSelected})
      setFormData({
        date: scheduleDateSelected || '',
        hour: scheduleHoursSelected || '',
      })
    } else {
      setFormData({ date: "", hour: "" });
    }
  }, [scheduleHoursSelected, scheduleDateSelected]);

  useEffect(() => {
    if (errors.date) {
      Notification.error(errors.date);
    } else if (errors.hour) {
      Notification.error(errors.hour);
    }
  }, [errors.date, errors.hour]);

  useEffect(() => {
    if (!!currentParams.get('clean')) {
      dispatch(clearTrip());
    }
  }, [currentParams])

  useEffect(() => {
    const id = getHashParam('id') || currentParams.get('id');

    dispatch(setIsUptimeEmergency(false));

    if (!id) return;

    fetchHasMechanicalEmergency(id)
      .then((isMechanicalEmergency) => {
        dispatch(setIsUptimeEmergency(isMechanicalEmergency));
      })
      .catch(() => {
        // keep false on error
      });
  }, []);

  return (
    <div className="display-flex flex-column gap-20 padding-y-20 padding-x-50 height-100pct">
      <div>
        <Title
          icon="rioglyph rioglyph-calendar"
          title="Definir data e hora"
        />
      </div>
      <div>
        <Progress.Trip
          selected={content}
          onSelectedChanged={handleContentChange}
          step={pageIndex}
          disabledIndex={pageIndex + 1}
          disabled={!isFormValid()}
          hideVehicle={isUptimeEmergency}
        />
      </div>

      <div className="bg-white rounded shadow-default padding-x-50 padding-y-20">
        <FormAgendamento
          formData={formData}
          errors={errors}
          onDateChange={handleDateChange}
          onHourChange={handleHourChange}
          onHourValidation={validateHourField}
        />

        <FooterActions
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isSubmitDisabled={!isFormValid()}
        />
      </div>
    </div>
  );
};

export default TripsSchedule;
